// AI 사용량 표시 컴포넌트 — 요약 카드, 일별 누적 막대, 호출 종류 비중 (외부 차트 라이브러리 없이 SVG)
import React, { useMemo, useState } from 'react';
import type { UsageDaily, UsageModel } from '../../api/adminUsage';
import {
  KIND_COLOR,
  KIND_DESC,
  REQUEST_COUNT_LABEL,
  TOKEN_COLOR,
  formatCompact,
  formatDayLabel,
  formatNumber,
  formatPercent,
  groupByKind,
} from './usageFormat';
import styles from './AiUsage.module.css';

/* 좌표계는 viewBox로 잡고 SVG는 폭 100%로 늘린다 — 툴팁은 %로 배치해 축척과 무관하게 붙는다 */
const W = 720;
const PAD = { top: 16, right: 14, bottom: 30, left: 62 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = 214;
const H = PAD.top + PLOT_H + PAD.bottom;
const BAR_MAX_W = 22;
/** x축 라벨 최대 개수 — 90일 프리셋에서 글자가 겹치지 않게 건너뛴다 */
const MAX_X_LABELS = 12;

/** 요약 수치 카드 — 숫자는 tabular-nums로 자릿수를 맞춘다 */
export function StatCard({ label, value, hint, accent }: {
  label: string;
  value: number;
  hint?: string;
  accent?: string;
}) {
  return (
    <div className={styles.statCard}>
      <span className={styles.statLabel}>
        {accent && <span className={styles.swatch} style={{ background: accent }} />}
        {label}
      </span>
      <span className={styles.statValue}>{formatNumber(value)}</span>
      {hint && <span className={styles.statHint}>{hint}</span>}
    </div>
  );
}

/** 눈금이 1·2·2.5·5·10의 배수로 떨어지게 상한을 올린다 */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(value)));
  const norm = value / exp;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return nice * exp;
}

interface Hover {
  /** 툴팁 위치 — SVG 좌표를 백분율로 바꿔 축척 변화에 흔들리지 않게 한다 */
  x: number;
  y: number;
  row: UsageDaily;
}

/**
 * 일별 추이 — 입력·출력을 하나의 막대에 쌓는다.
 * daily에는 임베딩 호출의 토큰도 섞여 있지만 건당 20토큰 안팎이라 눈에 띄지 않는다.
 * 종류별 비중은 옆의 UsageKindBreakdown에서 따로 본다.
 */
export function UsageDailyChart({ rows }: { rows: UsageDaily[] }) {
  const [hover, setHover] = useState<Hover | null>(null);
  // x축이 시간순이어야 추이로 읽힌다 — 응답 순서에 기대지 않는다
  const daily = useMemo(() => rows.slice().sort((a, b) => a.day.localeCompare(b.day)), [rows]);

  if (daily.length === 0) {
    return <p className={styles.chartEmpty}>이 기간에는 기록된 사용량이 없습니다.</p>;
  }

  const peak = daily.reduce((max, d) => Math.max(max, d.promptTokens + d.completionTokens), 0);
  const top = niceMax(peak);
  const y = (value: number) => PAD.top + PLOT_H - (value / top) * PLOT_H;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((r) => top * r);

  const bandW = PLOT_W / daily.length;
  const barW = Math.min(BAR_MAX_W, Math.max(2, bandW - 2));
  const labelStep = Math.ceil(daily.length / MAX_X_LABELS);

  return (
    <div className={styles.chartWrap}>
      <svg viewBox={`0 0 ${W} ${H}`} className={styles.chartSvg} role="img" aria-label="일별 토큰 사용 추이"
           onMouseLeave={() => setHover(null)}>
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={PAD.left} x2={PAD.left + PLOT_W} y1={y(tick)} y2={y(tick)}
                  stroke="#E2E8F0" strokeWidth="1" shapeRendering="crispEdges" />
            <text x={PAD.left - 8} y={y(tick) + 4} textAnchor="end" className={styles.axisText}>
              {formatCompact(Math.round(tick))}
            </text>
          </g>
        ))}

        {daily.map((row, i) => {
          const cx = PAD.left + bandW * (i + 0.5);
          const stackTop = y(row.promptTokens + row.completionTokens);
          const splitY = y(row.promptTokens);
          const baseline = y(0);
          return (
            <g key={row.day}>
              {/* 입력은 기준선 쪽, 출력은 그 위 — 순서를 바꾸면 범례와 어긋난다 */}
              <rect x={cx - barW / 2} y={splitY} width={barW} height={Math.max(0, baseline - splitY)}
                    fill={TOKEN_COLOR.prompt} />
              <rect x={cx - barW / 2} y={stackTop} width={barW} height={Math.max(0, splitY - stackTop)}
                    fill={TOKEN_COLOR.completion} />
              {/* 히트 영역은 밴드 전체 — 얇은 막대를 정확히 겨냥하게 하지 않는다 */}
              <rect x={PAD.left + bandW * i} y={PAD.top} width={bandW} height={PLOT_H} fill="transparent"
                    onMouseEnter={() => setHover({ x: (cx / W) * 100, y: (stackTop / H) * 100, row })} />
            </g>
          );
        })}

        <line x1={PAD.left} x2={PAD.left + PLOT_W} y1={y(0)} y2={y(0)}
              stroke="#CBD5E1" strokeWidth="1" shapeRendering="crispEdges" />

        {daily.map((row, i) => (
          i % labelStep === 0 ? (
            <text key={row.day} x={PAD.left + bandW * (i + 0.5)} y={PAD.top + PLOT_H + 20}
                  textAnchor="middle" className={styles.axisText}>
              {formatDayLabel(row.day)}
            </text>
          ) : null
        ))}
      </svg>

      {hover && (
        <div className={styles.tooltip} style={{ left: `${hover.x}%`, top: `${hover.y}%` }}>
          <div className={styles.tooltipDay}>{hover.row.day}</div>
          <div className={styles.tooltipRow}>
            <span className={styles.swatch} style={{ background: TOKEN_COLOR.prompt }} />
            <span className={styles.tooltipName}>입력</span>
            <span className={styles.tooltipValue}>{formatNumber(hover.row.promptTokens)}</span>
          </div>
          <div className={styles.tooltipRow}>
            <span className={styles.swatch} style={{ background: TOKEN_COLOR.completion }} />
            <span className={styles.tooltipName}>출력</span>
            <span className={styles.tooltipValue}>{formatNumber(hover.row.completionTokens)}</span>
          </div>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipName}>합계</span>
            <span className={styles.tooltipValue}>{formatNumber(hover.row.totalTokens)}</span>
          </div>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipName}>{REQUEST_COUNT_LABEL}</span>
            <span className={styles.tooltipValue}>{formatNumber(hover.row.requestCount)}</span>
          </div>
        </div>
      )}

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: TOKEN_COLOR.prompt }} />입력 토큰
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: TOKEN_COLOR.completion }} />출력 토큰
        </span>
      </div>
    </div>
  );
}

/**
 * 호출 종류 비중.
 * 임베딩은 건당 20토큰 안팎이라 같은 막대에 그리면 보이지 않는다 — 비중 막대는
 * 대화 생성(chat)만 그리고, 임베딩은 아래 표에 별도 행으로 뺀다.
 */
export function UsageKindBreakdown({ models }: { models: UsageModel[] }) {
  const groups = groupByKind(models);

  if (groups.length === 0) {
    return <p className={styles.chartEmpty}>이 기간에는 기록된 호출이 없습니다.</p>;
  }

  const chatGroups = groups.filter((group) => group.kind !== 'embedding');
  const embedding = groups.filter((group) => group.kind === 'embedding');
  const chatTokens = chatGroups.reduce((sum, group) => sum + group.totalTokens, 0);

  return (
    <div className={styles.breakdown}>
      {chatTokens > 0 && (
        <>
          <p className={styles.breakdownCaption}>대화 생성 토큰 비중 (임베딩 제외)</p>
          <div className={styles.shareBar}>
            {chatGroups.map((group) => (
              group.totalTokens > 0 ? (
                <div
                  key={group.kind}
                  className={styles.shareSegment}
                  style={{ width: `${(group.totalTokens / chatTokens) * 100}%`, background: KIND_COLOR[group.kind] }}
                  title={`${group.label} ${formatPercent(group.totalTokens, chatTokens)}`}
                />
              ) : null
            ))}
          </div>
          <div className={styles.legend}>
            {chatGroups.map((group) => (
              <span key={group.kind} className={styles.legendItem} title={KIND_DESC[group.kind]}>
                <span className={styles.swatch} style={{ background: KIND_COLOR[group.kind] }} />
                {group.label} {formatPercent(group.totalTokens, chatTokens)}
              </span>
            ))}
          </div>
        </>
      )}

      <table className={styles.kindTable}>
        <thead>
          <tr>
            <th>구분</th>
            <th className={styles.num}>토큰</th>
            <th className={styles.num}>{REQUEST_COUNT_LABEL}</th>
          </tr>
        </thead>
        <tbody>
          {chatGroups.map((group) => (
            <tr key={group.kind}>
              <td>
                <span className={styles.swatch} style={{ background: KIND_COLOR[group.kind] }} />
                <span className={styles.kindName} title={KIND_DESC[group.kind]}>{group.label}</span>
              </td>
              <td className={styles.num}>{formatNumber(group.totalTokens)}</td>
              <td className={styles.num}>{formatNumber(group.requestCount)}</td>
            </tr>
          ))}
          {embedding.map((group) => (
            <tr key={group.kind} className={styles.embeddingRow}>
              <td>
                <span className={styles.swatch} style={{ background: KIND_COLOR[group.kind] }} />
                <span className={styles.kindName} title={KIND_DESC[group.kind]}>{group.label}</span>
              </td>
              <td className={styles.num}>{formatNumber(group.totalTokens)}</td>
              <td className={styles.num}>{formatNumber(group.requestCount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {embedding.length > 0 && (
        <p className={styles.breakdownNote}>
          임베딩은 사내 문서를 찾기 위한 질의 벡터화입니다. 건당 20토큰 안팎이고 출력 토큰이 없어
          비중 막대에서는 제외했습니다.
        </p>
      )}

      <details className={styles.modelDetails}>
        <summary>모델별로 보기</summary>
        <table className={styles.kindTable}>
          <thead>
            <tr>
              <th>종류</th>
              <th>모델</th>
              <th className={styles.num}>토큰</th>
              <th className={styles.num}>{REQUEST_COUNT_LABEL}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => group.models.map((entry) => (
              <tr key={`${entry.operation}/${entry.model}`}>
                <td>
                  <span className={styles.swatch} style={{ background: KIND_COLOR[group.kind] }} />
                  <span className={styles.kindName}>{group.label}</span>
                </td>
                <td className={styles.modelName}>{entry.model}</td>
                <td className={styles.num}>{formatNumber(entry.totalTokens)}</td>
                <td className={styles.num}>{formatNumber(entry.requestCount)}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
